import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function CourseCarousel() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);

    // Course data array
    const courses = [
        {
            id: 1,
            title: "Guitar(Acoustic & Electric)",
            image: "images/courses-carousel/home-guitar.jpg",
            description: "Learn guitar with precision and style, from strumming rhythms to advanced solos. Our expert-led training covers fingerstyle, chords, improvisation, and stage techniques etc, for all levels of students, with a customized curriculum designed to match individual goals.",
            link: "/guitar",
        },
        {
            id: 2,
            title: "Piano/Keyboard",
            image: "images/courses-carousel/home-piano.jpg",
            description: "Build strong foundations in harmony, melody, and music theory while mastering piano or keyboard techniques. With sight-reading, ear training, and expression, this course empowers you to play confidently across genres, tailored for all levels and adapted to your personal goals.",
            link: "/piano",
        },
        {
            id: 3,
            title: "Opera Singing",
            image: "images/courses-carousel/home-opera.jpg",
            description: "Experience the rarest vocal art form with India's first Ultra Soprano Opera Singer - Opera Queen of India. Learn authentic European techniques, stagecraft, and repertoire in Italian, German, and French, accessible for all learners, with a personalized curriculum shaped around your artistic aspirations.",
            link: "/opera-singing",
        },
        {
            id: 4,
            title: "Western Contemporary Singing",
            image: "images/courses-carousel/home-western-vocals.png",
            description: "Explore modern vocal styles including pop, jazz, blues, and R&B with structured vocal training. The program builds pitch accuracy, vocal agility, microphone technique, stage presence and much more. Suitable for all levels, with training customized to fit your unique goals.",
            link: "/western-singing",
        },
        {
            id: 5,
            title: "Bollywood Film Singing",
            image: "images/courses-carousel/home-bollywood-film-singing.webp",
            description: "Master the art of playback singing and expressive performance through Bollywood's iconic styles. This course blends voice culture, diction, and emotion-driven delivery and much more. Crafted for all levels, with a flexible curriculum that aligns with your performance ambitions.",
            link: "/bollywood-singing",
        },
        {
            id: 6,
            title: "Hindustani Classical Singing",
            image: "images/courses-carousel/home-hindustani-classical-vocals.png",
            description: "Discover the depth of Indian ragas, improvisation, and traditional vocal techniques. Students train in aalap, taan, and compositions, building a classical foundation. Open to all levels, with a personalized path designed to achieve each learner's goals.",
            link: "/hindustani-singing",
        },
        {
            id: 7,
            title: "Violin",
            image: "images/courses-carousel/home-violin.png",
            description: "Learn violin with a structured approach covering posture, bowing, scales, and advanced techniques. The program develops classical and modern styles. Suitable for beginners to advanced learners, with a curriculum tailored to match personal musical goals.",
            link: "/violin",
        },
        {
            id: 8,
            title: "Music Production",
            image: "images/courses-carousel/home-music-production.jpg",
            description: "Step into the world of sound creation and artistry. Learn recording, mixing, mastering, and digital production techniques using industry-standard software and tools. From beginners to advanced learners, explore beat-making, composition, and arrangement with a personalized curriculum tailored to your creative goals—whether it’s producing your own songs, scoring films, or becoming a professional music producer.",
            link: "/music-production",
        },
        {
            id: 9,
            title: "Dual Program",
            image: "images/courses-carousel/home-dual-program.webp",
            description: "A flexible program designed for passionate learners who wish to explore more than one discipline. Train in any two instruments or voices. Available for all levels, with a fully customized learning path that balances both disciplines effectively.",
            link: "/dual-program",
        },
        {
            id: 10,
            title: "All-in-One Program",
            image: "images/courses-carousel/home-all-in-one.jpg",
            description: "Our most premium program for ambitious learners aiming for all-round mastery. Choose any three instruments or voices with specialized lessons, and stage training. Designed for all levels, with personalized goals shaping your complete growth.",
            link: "/all-in-one",
        }
    ];

    // Function to go to next slide
    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % courses.length);
    };

    // Function to go to previous slide
    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + courses.length) % courses.length);
    };

    // Auto-play functionality (optional)
    useEffect(() => {
        if (!isPaused) {
            intervalRef.current = setInterval(() => {
                nextSlide();
            }, 2000); // change slide every 2s
        }

        return () => clearInterval(intervalRef.current);
    }, [isPaused, currentSlide]);

    return (
        <>
            <section id="courses-carousel-section">
                <div 
                className="carousel-container"
                onMouseEnter={() => setIsPaused(true)}   // ✅ pause on hover
                onMouseLeave={() => setIsPaused(false)}  // ✅ resume on leave
                >
                    <div className="carousel-header">
                        <h2 className="section-title">Our Courses</h2>
                        <p className="section-subtitle">Discover the perfect music course for you</p>
                    </div>

                    <div className="custom-carousel">
                        <div className="carousel-slides">
                            {courses.map((course, index) => (
                                <div
                                    key={course.id}
                                    className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                                    data-slide={course.id}
                                >
                                    <div className="slide-content">
                                        <div className="slide-image">
                                            <div className="course-title-container">
                                                <h3 className="course-title">{course.title}</h3>
                                            </div>
                                            <img src={course.image} alt={`${course.title} Course`} />
                                        </div>
                                        <div className="slide-info">
                                            <h3 className="course-title">{course.title}</h3>
                                            <p className="course-description">{course.description}</p>
                                            <div className="course-details">
                                                <span className="duration"><Link to={`${course.link}`}>Read More</Link></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button className="carousel-arrow prev" onClick={prevSlide}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <button className="carousel-arrow next" onClick={nextSlide}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
}